import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

const STEPS = [
  "Our model is getting ready for your jewellery photoshoot.",
  "Studio lights and camera angles are being set for the perfect shine.",
  "The model is adjusting pose to highlight your jewellery beautifully.",
  "We're taking a few test shots to check lighting and reflections.",
  "Final touches are being added for the perfect jewellery look.",
  "Perfect! Your photoshoot is ready — let's create stunning visuals. ✦",
];

const FILLERS = [
  "Refining the golden hour glow on your pieces…",
  "Calibrating gem sparkle and metal reflections…",
  "Adjusting the model's hand position for a natural touch…",
  "Dialing in the soft studio light on each facet…",
  "Perfecting depth of field around your jewellery…",
  "Rendering silk fabric drape and texture detail…",
  "Balancing skin tone warmth against the gold…",
  "Checking necklace placement on the chest plane…",
  "Applying Rembrandt light for that editorial look…",
  "Fine-tuning shadow softness and highlight intensity…",
  "Ensuring every diamond facet catches the light…",
  "Composing the final bust-shot crop…",
  "Almost there — our AI is putting on the finishing touches…",
  "This one's looking especially stunning, worth the wait…",
  "Running a final editorial eye over your campaign…",
  "Your luxury campaign is nearly camera-ready…",
  "Adding that signature Glammify luminosity…",
  "Double-checking gem geometry — no distortion, ever…",
  "Still rendering — your visuals will be worth every second…",
  "Our model is taking one last look in the mirror…",
  "Complex reflections take a moment to perfect…",
  "The AI studio is putting the final polish on your visuals…",
  "Great campaigns are never rushed — almost ready…",
  "Applying one last pass of editorial quality control…",
  "Just a few more moments for perfection…",
];

const STEP_MS = 5000;
const FILLER_MS = 4500;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const gemPulse = keyframes`
  0%, 100% { filter: drop-shadow(0 0 5px rgba(98,24,197,.65)); transform: translate(-50%,-50%) scale(1); }
  50% { filter: drop-shadow(0 0 14px rgba(212,175,55,.85)); transform: translate(-50%,-50%) scale(1.12); }
`;

const pfloat = keyframes`
  0% { opacity: 0; transform: translate(0,0) scale(0); }
  25% { opacity: .7; }
  100% { opacity: 0; transform: translate(var(--tx), var(--ty)) scale(1.3); }
`;

const LoaderContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  zIndex: 1,
});

const BrandWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  marginBottom: '40px',
});

const BrandName = styled('span')({
  fontSize: '14px',
  fontWeight: 600,
  letterSpacing: '.3em',
  textTransform: 'uppercase',
  background: 'linear-gradient(135deg, #6218C5 0%, #8B5CF6 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
});

const BrandDot = styled('div')({
  width: '4px',
  height: '4px',
  borderRadius: '50%',
  backgroundColor: '#D4AF37',
  flexShrink: 0,
});

const LoadingPanel = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const SpinnerWrap = styled(Box)({
  position: 'relative',
  width: '150px',
  height: '150px',
  marginBottom: '36px',
});

const RingOuter = styled(Box)({
  position: 'absolute',
  inset: 0,
  borderRadius: '50%',
  border: '1.5px solid transparent',
  borderTopColor: '#6218C5',
  borderRightColor: '#8B5CF6',
  animation: `${spin} 1.6s linear infinite`,
});

const RingInner = styled(Box)({
  position: 'absolute',
  inset: '14px',
  borderRadius: '50%',
  border: '1px solid transparent',
  borderTopColor: '#D4AF37',
  borderLeftColor: '#E8CB5A',
  animation: `${spin} 2.4s linear infinite reverse`,
});

const SpinnerGem = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%,-50%)',
  animation: `${gemPulse} 2s ease-in-out infinite`,
});

const ParticlesContainer = styled(Box)({
  position: 'absolute',
  inset: '-50px',
  pointerEvents: 'none',
  overflow: 'visible',
});

const ParticleBase = styled('div')({
  position: 'absolute',
  borderRadius: '50%',
  opacity: 0,
});

const StepText = styled('div')(({ out_anim, in_anim }) => ({
  fontSize: '15px',
  fontWeight: 400,
  color: '#111111',
  padding: '0 18px',
  lineHeight: 1.65,
  maxWidth: '340px',
  minHeight: '60px',
  marginBottom: '30px',
  transition: 'opacity .35s ease, transform .35s ease',
  textAlign: 'center',
  fontFamily: "var(--font-montserrat)",
  ...(out_anim === 1 && { opacity: 0, transform: 'translateY(-6px)' }),
  ...(in_anim === 1 && { opacity: 1, transform: 'translateY(0)' }),
  ...(out_anim === 0 && in_anim === 0 && { opacity: 0, transform: 'translateY(6px)' }),
}));

const DotsRow = styled('div')({
  display: 'flex',
  gap: '8px',
  justifyContent: 'center',
  marginBottom: '22px',
});

const Dot = styled('div')(({ active, done }) => ({
  width: '7px',
  height: '7px',
  borderRadius: '50%',
  background: '#DDD',
  transition: 'all .4s ease',
  ...(active === 1 && { background: '#6218C5', transform: 'scale(1.4)', boxShadow: '0 0 8px rgba(98,24,197,.4)' }),
  ...(done === 1 && { background: '#A78BFA' }),
}));

const GlammifyAnimationLoader = () => {
  const [particles, setParticles] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [fillerIndex, setFillerIndex] = useState(0);
  const [isUsingFillers, setIsUsingFillers] = useState(false);
  const [currentText, setCurrentText] = useState('');
  const [animState, setAnimState] = useState({ out_anim: false, in_anim: false });
  const [internalProgress, setInternalProgress] = useState(0);

  useEffect(() => {
    const colors = ['#6218C5', '#8B5CF6', '#A78BFA', '#D4AF37', '#E8CB5A'];
    const newParticles = [];
    for (let i = 0; i < 20; i++) {
      const sz = 3 + Math.random() * 4;
      newParticles.push({
        id: i,
        sz,
        left: 10 + Math.random() * 80,
        top: 20 + Math.random() * 60,
        background: colors[Math.floor(Math.random() * colors.length)],
        duration: 2 + Math.random() * 3,
        delay: Math.random() * 3,
        tx: -50 + Math.random() * 100,
        ty: -40 - Math.random() * 70,
      });
    }
    setParticles(newParticles);

    // Initial start
    changeText(STEPS[0]);
    // Start initial delay properly
    const timeout = setTimeout(() => {
      setStepIndex(1);
    }, STEP_MS);

    return () => clearTimeout(timeout);
  }, []);

  const changeText = (newText) => {
    setAnimState({ out_anim: true, in_anim: false });
    setTimeout(() => {
      setCurrentText(newText);
      setAnimState({ out_anim: false, in_anim: true });
    }, 400);
  };

  useEffect(() => {
    let timer;
    if (!isUsingFillers) {
      if (stepIndex > 0 && stepIndex < STEPS.length) {
        setInternalProgress(((stepIndex + 1) / STEPS.length) * 100);
        changeText(STEPS[stepIndex]);
        timer = setTimeout(() => {
          setStepIndex(prev => prev + 1);
        }, STEP_MS);
      } else if (stepIndex >= STEPS.length) {
        setIsUsingFillers(true);
      }
    } else {
      setInternalProgress(100);
      changeText(FILLERS[fillerIndex % FILLERS.length]);
      timer = setTimeout(() => {
        setFillerIndex(prev => prev + 1);
      }, FILLER_MS);
    }
    return () => clearTimeout(timer);
  }, [stepIndex, isUsingFillers, fillerIndex]);

  return (
    <LoaderContainer className='loader-card'>
      {/* BRAND */}
      <BrandWrapper>
        <svg style={{ width: '20px', height: '20px', flexShrink: 0 }} viewBox="0 0 20 20" fill="none">
          <polygon points="10,1 19,7 15.5,19 4.5,19 1,7" fill="none" stroke="url(#loader-bg)" strokeWidth="1.2" />
          <polygon points="10,1 19,7 10,10" fill="rgba(98,24,197,.3)" />
          <polygon points="10,1 1,7 10,10" fill="rgba(139,92,246,.2)" />
          <polygon points="1,7 10,10 4.5,19" fill="rgba(98,24,197,.15)" />
          <polygon points="19,7 15.5,19 10,10" fill="rgba(139,92,246,.25)" />
          <defs>
            <linearGradient id="loader-bg" x1="0" y1="0" x2="20" y2="20" gradientUnits="userSpaceOnUse">
              <stop stopColor="#6218C5" /><stop offset="1" stopColor="#8B5CF6" />
            </linearGradient>
          </defs>
        </svg>
        <BrandName>Glammify Studio</BrandName>
        <BrandDot />
      </BrandWrapper>

      {/* LOADING */}
      <LoadingPanel>
        <Box sx={{ position: 'relative' }}>
          <ParticlesContainer>
            {particles.map((p) => (
              <ParticleBase
                key={p.id}
                style={{
                  width: `${p.sz}px`,
                  height: `${p.sz}px`,
                  left: `${p.left}%`,
                  top: `${p.top}%`,
                  background: p.background,
                  '--tx': `${p.tx}px`,
                  '--ty': `${p.ty}px`,
                  animation: `${pfloat} ${p.duration}s ease-in-out ${p.delay}s infinite`
                }}
              />
            ))}
          </ParticlesContainer>
          <SpinnerWrap>
            <RingOuter />
            <RingInner />
            <SpinnerGem>
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <polygon points="16,3 29,11 25,29 7,29 3,11" fill="none" stroke="url(#loader-sg)" strokeWidth="1.2" />
                <polygon points="16,3 29,11 16,16" fill="rgba(98,24,197,.35)" />
                <polygon points="16,3 3,11 16,16" fill="rgba(167,139,250,.25)" />
                <polygon points="3,11 16,16 7,29" fill="rgba(98,24,197,.2)" />
                <polygon points="29,11 25,29 16,16" fill="rgba(139,92,246,.3)" />
                <polygon points="16,3 20,11 16,11" fill="rgba(212,175,55,.55)" />
                <defs>
                  <linearGradient id="loader-sg" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#6218C5" /><stop offset=".5" stopColor="#A78BFA" /><stop offset="1" stopColor="#D4AF37" />
                  </linearGradient>
                </defs>
              </svg>
            </SpinnerGem>
          </SpinnerWrap>
        </Box>

        <StepText out_anim={animState.out_anim ? 1 : 0} in_anim={animState.in_anim ? 1 : 0}>
          {currentText}
        </StepText>

        <DotsRow>
          {STEPS.map((_, i) => {
            let done = false;
            let active = false;

            if (isUsingFillers) {
              done = true;
            } else {
              if (i < stepIndex) done = true;
              else if (i === stepIndex) active = true;
            }

            return <Dot key={i} done={done ? 1 : 0} active={active ? 1 : 0} />;
          })}
        </DotsRow>

      </LoadingPanel>
    </LoaderContainer>
  );
};

export default GlammifyAnimationLoader;
